import { Link } from "wouter";
import { Home, SettingsIcon, Filter as FilterIcon } from "lucide-react";
import FolderStructure from "@/components/documents/FolderStructure";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useEffect } from "react";
import { useAppStore } from "@/lib/store";
import { useAdminProject } from "@/hooks/useAdminProject";
import { useSidebarLayout } from "@/hooks/useSidebarLayout";

export default function Documents() {
  // Get project information from URL and store
  const { projectId, selectedProject, loading: projectLoading } = useAdminProject();
  const { getPageHeaderClass } = useSidebarLayout();
  const { setSelectedProject } = useAppStore();
  
  // Check if Autodesk connection is configured
  const { data: connectionStatus, isLoading: connectionLoading } = useQuery({
    queryKey: ["/api/activities-data", "connection"],
    queryFn: () => apiRequest("/api/activities-data?type=connection"),
  });

  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Fixed Page Header - single line with button */}
      <header className={getPageHeaderClass()}>
        <div className="h-full flex items-center justify-between px-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-gray-900">Project Documents</h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Filter Files 按钮 */}
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center"
              onClick={() => {
                // 触发自定义事件，让 FolderStructure 组件响应
                window.dispatchEvent(new CustomEvent('toggleFilterPanel'));
              }}
            >
              <FilterIcon className="h-4 w-4 mr-1" />
              Filter Files
            </Button>

          </div>
        </div>
      </header>
      
      {/* Main Content with top padding to account for both headers (64px global + 64px page = 128px = pt-32) */}
      <main className="flex-1 min-h-0 h-full flex flex-col pt-32 overflow-hidden">
        {/* OAuth connection is working - no configuration needed */}

        {/* Show loading state */}
        {projectLoading || connectionLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading documents...</p>
            </div>
          </div>
        ) : !selectedProject ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Project Selected</h2>
              <p className="text-gray-600 mb-4">Please select a project to view documents</p>
              <Link href="/myhome">
                <Button>
                  <Home className="h-4 w-4 mr-2" />
                  Go to My Home
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          /* Folder structure component - full width and self-contained */
          <div className="flex-1 min-h-0 h-full flex flex-col overflow-hidden">
            <FolderStructure />
          </div>
        )}
      </main>
    </div>
  );
}
